import type { Event } from '../contexts/EventContext';

/** pool id(string) → 표시 이름 */
export type PoolNameMap = Record<string, string>;
/** cctv id(string) → 위치 라벨(예: 1-1) */
export type CctvLocationMap = Record<string, string>;

export function resolvePoolName(
  poolId: string | number,
  poolNames?: PoolNameMap,
  fallbackName?: string | null,
): string {
  const key = String(poolId);
  const fromMap = String(poolNames?.[key] || '').trim();
  if (fromMap) return fromMap;
  const fb = String(fallbackName || '').trim();
  if (fb && fb !== '-' && fb !== '—' && !/^시설\s*#?\d+$/i.test(fb)) {
    return fb;
  }
  return `시설 #${key}`;
}

export function enrichEventsWithPoolNames(events: Event[], poolNames?: PoolNameMap): Event[] {
  if (!poolNames || Object.keys(poolNames).length === 0) {
    return events.map((e) => ({
      ...e,
      poolName: resolvePoolName(e.poolId, undefined, e.poolName),
    }));
  }
  return events.map((e) => ({
    ...e,
    poolName: resolvePoolName(e.poolId, poolNames, e.poolName),
  }));
}

export function enrichEventsWithCctvLocations(events: Event[], cctvLocations?: CctvLocationMap): Event[] {
  if (!cctvLocations || Object.keys(cctvLocations).length === 0) return events;
  return events.map((e) => {
    const rawId = e.metadata?.cctv_id;
    const code = e.metadata?.cctv_code != null ? String(e.metadata.cctv_code).trim() : '';
    const mapped =
      (rawId != null ? cctvLocations[String(rawId)] : undefined) ||
      (code && e.poolId
        ? cctvLocations[`pool:${e.poolId}:code:${code.toUpperCase()}`]
        : undefined);
    if (!mapped) return e;
    return {
      ...e,
      location: mapped,
      metadata: {
        ...(e.metadata || {}),
        risk_evidence_location: mapped,
      },
    };
  });
}

export type ApiEventRow = {
  id: number | string;
  event_db_id?: number | null;
  ack_id?: string | number;
  client_event_id?: string | null;
  pool_id: number | string;
  /** 시설 표시명 (API가 내려주면 맵보다 우선) */
  pool_name?: string | null;
  member_id?: number | null;
  wearable_id?: number | null;
  event_type: string;
  risk_level?: string;
  severity?: string;
  status: string;
  location?: string | null;
  description?: string | null;
  details?: string | null;
  image_url?: string | null;
  created_at: string;
  updated_at?: string;
  /** 위험 감지 unix 초 (API가 직접 내려줄 때) */
  trigger_ts?: number | null;
};

function mapStatus(apiStatus: string): Event['status'] {
  const s = (apiStatus || '').toLowerCase();
  if (s === 'resolved' || s === 'auto_resolved') return 'resolved';
  if (s === 'active') return 'new';
  return 'processing';
}

function isNoiseEventType(eventType?: string | null): boolean {
  const t = String(eventType || '').trim().toLowerCase();
  if (!t) return true;
  return [
    'drowning',
    'drowning_risk',
    'drowning_risk_motion',
    'motion_risk',
    'no_motion',
    'sinking',
    'manual',
    'safety_alert',
    'risk',
  ].includes(t);
}

function mapType(eventType: string, riskLevel: string): Event['type'] {
  const t = (eventType || '').toLowerCase();
  const r = (riskLevel || '').toLowerCase();
  if (t.includes('danger') || t.includes('risk') || t.includes('drown') || r.includes('긴급') || r.includes('위험')) {
    return 'danger';
  }
  if (t.includes('warn') || r.includes('주의')) return 'warning';
  return 'info';
}

function parseDetails(details?: string | null): {
  location: string;
  description: string;
  cctv_id?: number;
  cctv_code?: string;
  event_type_hint?: string;
  risk_type?: string;
  risk_label?: string;
  risk_labels?: string[];
  risk_items?: Array<{ label: string; description?: string }>;
  risk_evidence_location?: string;
  image_url?: string;
  has_snapshot?: boolean;
  source?: string;
  /** 실제 위험 감지 시각 (unix sec) */
  trigger_ts?: number;
  occurred_at?: string;
} {
  if (!details) return { location: '', description: '', risk_evidence_location: undefined };
  try {
    const d = JSON.parse(details);
    if (typeof d === 'object' && d) {
      const loc =
        d.location ||
        (d.cctv_id != null ? `CCTV ${d.cctv_id}` : '') ||
        (d.pool_id != null ? `pool ${d.pool_id}` : '');
      const riskItems = Array.isArray(d.risk_items)
        ? d.risk_items
            .filter((x: unknown) => x && typeof x === 'object')
            .map((x: { label?: unknown; description?: unknown }) => ({
              label: String(x.label || '').trim(),
              description: String(x.description || '').trim() || undefined,
            }))
            .filter((x: { label: string }) => Boolean(x.label))
        : undefined;
      let riskLabel =
        typeof d.risk_label === 'string' && d.risk_label.trim()
          ? d.risk_label.trim()
          : Array.isArray(d.risk_labels) && d.risk_labels.length > 0
            ? String(d.risk_labels[0]).trim()
            : undefined;
      let riskLabels = Array.isArray(d.risk_labels)
        ? d.risk_labels.map((x: unknown) => String(x).trim()).filter(Boolean)
        : riskLabel
          ? [riskLabel]
          : undefined;
      // risk_items(bbox별)에만 있는 코드명도 목록에 반영
      if (riskItems?.length) {
        const merged = [...(riskLabels || [])];
        for (const it of riskItems) {
          if (it.label && !merged.includes(it.label)) merged.push(it.label);
        }
        riskLabels = merged.length ? merged : riskLabels;
        if (!riskLabel && merged[0]) riskLabel = merged[0];
      }
      const desc =
        d.risk_message ||
        d.message ||
        riskLabel ||
        (Array.isArray(d.risk_causes) ? d.risk_causes.join(', ') : '') ||
        details;
      const re = d.risk_evidence;
      const cctvIdFromEvidence =
        re && typeof re === 'object' && re.cctv_id != null ? Number(re.cctv_id) : undefined;
      const riskLoc =
        (re && typeof re === 'object' && re.location != null
          ? String(re.location)
          : undefined);
      const imageFromDetails =
        typeof d.image_url === 'string' && d.image_url.trim()
          ? String(d.image_url).trim()
          : undefined;
      const hasSnapshot = Boolean(
        imageFromDetails ||
          d.snapshot_filename ||
          (re && typeof re === 'object' && re.snapshot_filename)
      );
      let triggerTs: number | undefined;
      if (d.trigger_ts != null && Number.isFinite(Number(d.trigger_ts))) {
        triggerTs = Number(d.trigger_ts);
      } else if (
        re &&
        typeof re === 'object' &&
        re.trigger_ts != null &&
        Number.isFinite(Number(re.trigger_ts))
      ) {
        triggerTs = Number(re.trigger_ts);
      }
      return {
        location: String(loc),
        // 목록·상세에서 bbox 줄까지 쓰므로 과도한 절단 완화
        description: String(desc).slice(0, 4000),
        cctv_id: d.cctv_id != null ? Number(d.cctv_id) : cctvIdFromEvidence,
        cctv_code:
          typeof d.cctv_code === 'string'
            ? d.cctv_code
            : typeof d.camera_code === 'string'
              ? d.camera_code
              : typeof d.location === 'string' && /^CCTV-\d+$/i.test(d.location)
                ? d.location
                : typeof riskLoc === 'string' && /^CCTV-\d+$/i.test(riskLoc)
                  ? riskLoc
                  : undefined,
        event_type_hint: typeof d.event_type === 'string' ? d.event_type : undefined,
        risk_type: typeof d.risk_type === 'string' ? d.risk_type : undefined,
        risk_label: riskLabel,
        risk_labels: riskLabels,
        risk_items: riskItems,
        risk_evidence_location: riskLoc,
        image_url: imageFromDetails,
        has_snapshot: hasSnapshot,
        source: typeof d.source === 'string' ? d.source : undefined,
        trigger_ts: triggerTs,
        occurred_at:
          typeof d.occurred_at === 'string' && d.occurred_at.trim()
            ? d.occurred_at.trim()
            : undefined,
      };
    }
  } catch {
    /* plain text */
  }
  return { location: '', description: details.slice(0, 4000), risk_evidence_location: undefined };
}

function resolveEventOccurrenceTime(
  createdAt: string,
  triggerTs?: number,
  occurredAt?: string
): Date {
  if (triggerTs != null && Number.isFinite(triggerTs)) {
    const ms = triggerTs > 1e12 ? triggerTs : triggerTs * 1000;
    const d = new Date(ms);
    if (Number.isFinite(d.getTime())) return d;
  }
  if (occurredAt) {
    const d = new Date(occurredAt);
    if (Number.isFinite(d.getTime())) return d;
  }
  return new Date(createdAt);
}

export function mapApiEventToUi(row: ApiEventRow, poolNames?: PoolNameMap): Event {
  const {
    location,
    description,
    cctv_id,
    cctv_code,
    risk_evidence_location,
    risk_type,
    image_url: detailsImageUrl,
    has_snapshot,
    source,
    trigger_ts,
    occurred_at,
    risk_label,
    risk_labels,
    risk_items,
  } = parseDetails(row.details);
  const apiTrigger =
    row.trigger_ts != null && Number.isFinite(Number(row.trigger_ts))
      ? Number(row.trigger_ts)
      : undefined;
  const resolvedTriggerTs = apiTrigger ?? trigger_ts;
  const resolvedRiskLevel = row.risk_level || row.severity || '';
  const resolvedLocation = location || String(row.location || '');
  const resolvedDescription = description || String(row.description || '');
  const displayId = row.client_event_id
    ? String(row.client_event_id)
    : String(row.ack_id ?? row.id);
  const dbId =
    row.event_db_id != null && Number.isFinite(Number(row.event_db_id))
      ? Number(row.event_db_id)
      : Number.isFinite(Number(row.ack_id))
        ? Number(row.ack_id)
        : Number.isFinite(Number(row.id))
          ? Number(row.id)
          : row.id;

  let imageUrl =
    (typeof row.image_url === 'string' && row.image_url.trim()
      ? row.image_url.trim()
      : undefined) || detailsImageUrl;
  const expectSnapshot =
    has_snapshot ||
    source === 'safety_alert' ||
    (cctv_id != null &&
      /drown|risk|sink|no_motion|fall|collision|crowd|slip|manual/i.test(
        String(row.event_type || risk_type || '')
      ));
  if (
    !imageUrl &&
    expectSnapshot &&
    Number.isFinite(Number(dbId)) &&
    Number(dbId) > 0
  ) {
    imageUrl = `/api/events/${dbId}/risk-snapshot`;
  }

  const titleFromLabel =
    (risk_label && risk_label.trim()) ||
    (Array.isArray(risk_labels) && risk_labels[0] ? String(risk_labels[0]).trim() : '');

  return {
    id: displayId,
    ack_id: row.ack_id ?? row.id,
    client_event_id: row.client_event_id ?? null,
    type: mapType(row.event_type, resolvedRiskLevel),
    title: titleFromLabel || (isNoiseEventType(row.event_type) ? '이벤트' : row.event_type) || '이벤트',
    description: resolvedDescription || titleFromLabel || '',
    poolId: String(row.pool_id),
    poolName: resolvePoolName(row.pool_id, poolNames, row.pool_name),
    location: resolvedLocation || '—',
    // 증거 영상과 동일: 감지 시각(trigger_ts) 우선, 없으면 DB 저장 시각
    timestamp: resolveEventOccurrenceTime(
      row.created_at,
      resolvedTriggerTs,
      occurred_at
    ),
    imageUrl,
    status: mapStatus(row.status),
    isRead: mapStatus(row.status) === 'resolved',
    wearableId: row.wearable_id != null ? String(row.wearable_id) : undefined,
    personId: row.member_id != null ? String(row.member_id) : undefined,
    metadata: {
      event_db_id: dbId,
      event_type: row.event_type,
      risk_type,
      risk_label: risk_label || titleFromLabel || undefined,
      risk_labels,
      risk_items,
      cctv_id,
      cctv_code,
      risk_level: resolvedRiskLevel,
      risk_evidence_location,
      trigger_ts: resolvedTriggerTs,
      created_at: row.created_at,
    },
  };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/** 백엔드 풀 WS flat payload(risk_alert 등) → 관제 UI Event */
export function mapWebSocketPayloadToUi(
  data: unknown,
  poolNames?: PoolNameMap,
): Event | null {
  if (!isRecord(data)) return null;

  const msgType = String(data.type ?? '');

  if (msgType === 'event') {
    const poolId = data.pool_id != null ? String(data.pool_id) : '';
    const clientId = data.event_id != null ? String(data.event_id) : '';
    const rawTs = data.timestamp ?? data.resolved_at;
    const timestamp =
      typeof rawTs === 'number'
        ? rawTs > 1e12
          ? new Date(rawTs)
          : new Date(rawTs * 1000)
        : new Date(String(rawTs ?? Date.now()));
    const riskLevel = String(data.risk_level ?? '');
    const eventType = String(data.event_type ?? '이벤트');
    const member = isRecord(data.member) ? data.member : null;
    return {
      id: clientId || `ws-event-${poolId}-${timestamp.getTime()}`,
      ack_id: data.ack_id as string | number | undefined,
      client_event_id: clientId || null,
      type: mapType(eventType, riskLevel),
      title: eventType,
      description: riskLevel,
      poolId,
      poolName: poolId
        ? resolvePoolName(
            poolId,
            poolNames,
            data.pool_name != null ? String(data.pool_name) : undefined,
          )
        : '—',
      location: data.object_id != null ? `객체 ${data.object_id}` : '—',
      timestamp,
      status: 'new',
      isRead: false,
      personId: member?.id != null ? String(member.id) : undefined,
      personName: member?.name != null ? String(member.name) : undefined,
      metadata: data,
    };
  }

  if (msgType === 'event_resolved') {
    const poolId = data.pool_id != null ? String(data.pool_id) : '';
    const eventId = String(data.event_id ?? '');
    return {
      id: eventId || `ws-resolved-${Date.now()}`,
      type: 'info',
      title: '이벤트 해결',
      description: String(data.resolve_type ?? 'resolved'),
      poolId,
      poolName: poolId
        ? resolvePoolName(
            poolId,
            poolNames,
            data.pool_name != null ? String(data.pool_name) : undefined,
          )
        : '—',
      location: '—',
      timestamp: new Date(String(data.resolved_at ?? Date.now())),
      status: 'resolved',
      isRead: true,
      metadata: data,
    };
  }

  if (msgType === 'risk_alert' || msgType === 'risk_event') {
    const poolId = data.pool_id != null ? String(data.pool_id) : '';
    const cctvId = data.cctv_id;
    const rawTs = data.timestamp;
    let timestamp: Date;
    if (typeof rawTs === 'number') {
      timestamp = rawTs > 1e12 ? new Date(rawTs) : new Date(rawTs * 1000);
    } else if (rawTs != null) {
      timestamp = new Date(String(rawTs));
    } else {
      timestamp = new Date();
    }
    const riskLevel = String(data.risk_level ?? '');
    const stableKey = `${poolId}-${cctvId ?? 'x'}-${Math.floor(timestamp.getTime() / 1000)}`;
    return {
      id: `ws-${stableKey}`,
      type: mapType('risk', riskLevel),
      title: riskLevel || '위험 알림',
      description: String(data.risk_message ?? ''),
      poolId,
      poolName: poolId
        ? resolvePoolName(
            poolId,
            poolNames,
            data.pool_name != null ? String(data.pool_name) : undefined,
          )
        : '—',
      location: cctvId != null ? `CCTV ${cctvId}` : '—',
      timestamp,
      status: 'new',
      isRead: false,
      metadata: data as Record<string, unknown>,
    };
  }

  if (data.pool_id != null && (data.event_type != null || data.risk_level != null)) {
    return mapApiEventToUi(data as unknown as ApiEventRow, poolNames);
  }

  if (data.poolId != null && data.id != null) {
    const ts = data.timestamp;
    return {
      id: String(data.id),
      ack_id: data.ack_id as string | number | undefined,
      client_event_id: (data.client_event_id as string | null) ?? null,
      type: (data.type as Event['type']) || 'info',
      title: String(data.title ?? '이벤트'),
      description: String(data.description ?? ''),
      poolId: String(data.poolId),
      poolName: String(data.poolName ?? resolvePoolName(String(data.poolId), poolNames)),
      location: String(data.location ?? '—'),
      timestamp: ts instanceof Date ? ts : new Date(String(ts ?? Date.now())),
      status: (data.status as Event['status']) || 'new',
      isRead: Boolean(data.isRead),
      imageUrl: data.imageUrl as string | undefined,
      personId: data.personId != null ? String(data.personId) : undefined,
      personName: data.personName != null ? String(data.personName) : undefined,
      wearableId: data.wearableId != null ? String(data.wearableId) : undefined,
      metadata: isRecord(data.metadata) ? data.metadata : undefined,
    };
  }

  return null;
}
